/**
 * NerdSkillsEditor — CRUD de NerdCategory (skill matrix).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Star, Tag, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { categoriesApi } from "@/lib/api-cadastros";
import { nerdSkillsApi } from "@/lib/api-bulk";
import { cn } from "@/lib/utils";

export function NerdSkillsEditor({ nerdId }: { nerdId: string }) {
  const qc = useQueryClient();
  const [addingCategoryId, setAddingCategoryId] = useState<string>("");
  const [addingLevel, setAddingLevel] = useState<number>(3);

  const skills = useQuery({
    queryKey: ["nerd-skills", nerdId],
    queryFn: () => nerdSkillsApi.list(nerdId),
    enabled: !!nerdId,
  });
  const categories = useQuery({
    queryKey: ["categories-mini"],
    queryFn: () => categoriesApi.list({ size: 200 }),
  });

  const upsert = useMutation({
    mutationFn: ({ catId, level }: { catId: string; level: number }) =>
      nerdSkillsApi.upsert(nerdId, catId, level),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nerd-skills", nerdId] });
      setAddingCategoryId("");
      setAddingLevel(3);
      toast.success("Skill salva");
    },
  });

  const remove = useMutation({
    mutationFn: (catId: string) => nerdSkillsApi.remove(nerdId, catId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nerd-skills", nerdId] });
      toast.success("Removida");
    },
  });

  const usedCategoryIds = new Set(skills.data?.map((s) => s.category_id) ?? []);
  const available = (categories.data?.items ?? []).filter((c) => !usedCategoryIds.has(c.id));

  return (
    <div className="space-y-4">
      {/* Add new */}
      <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4">
        <p className="text-sm font-medium mb-3">Adicionar skill</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2 items-end">
          <Select value={addingCategoryId} onValueChange={setAddingCategoryId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione categoria" />
            </SelectTrigger>
            <SelectContent>
              {available.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(addingLevel)} onValueChange={(v) => setAddingLevel(Number(v))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5].map((lv) => (
                <SelectItem key={lv} value={String(lv)}>
                  Nível {lv} {"⭐".repeat(lv)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => upsert.mutate({ catId: addingCategoryId, level: addingLevel })}
            disabled={!addingCategoryId || upsert.isPending}
          >
            <Plus className="h-4 w-4" /> Adicionar
          </Button>
        </div>
      </div>

      {/* Lista atual */}
      {skills.data && skills.data.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="Sem skills cadastradas"
          description="Adicione categorias que o técnico domina."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {skills.data?.map((s) => (
            <SkillRow
              key={s.category_id}
              skill={s}
              onChange={(level) => upsert.mutate({ catId: s.category_id, level })}
              onRemove={() => remove.mutate(s.category_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SkillRow({
  skill,
  onChange,
  onRemove,
}: {
  skill: { category_name: string | null; level: number };
  onChange: (level: number) => void;
  onRemove: () => void;
}) {
  return (
    <div className="group rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-3 flex items-center gap-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-500/10">
        <Tag className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm truncate">{skill.category_name ?? "—"}</p>
        <div className="flex items-center gap-0.5 mt-0.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className="transition-transform hover:scale-110"
            >
              <Star
                className={cn(
                  "h-3.5 w-3.5",
                  n <= skill.level
                    ? "fill-amber-500 text-amber-500"
                    : "text-slate-300 dark:text-white/15",
                )}
              />
            </button>
          ))}
          <Badge variant="muted" size="sm" className="ml-1.5 text-[10px]">{skill.level}/5</Badge>
        </div>
      </div>
      <Button
        size="icon-sm"
        variant="ghost"
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
